import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, CalendarDaysIcon, ChevronRightIcon, ClipboardListIcon, DatabaseIcon, LayoutDashboardIcon, LogOutIcon, PanelLeftCloseIcon, PanelLeftOpenIcon, PackageIcon, SettingsIcon, UserRoundIcon, UsersIcon } from 'lucide-react';
import { Button } from './Button';
import { Card } from './Card';
import { Logo } from './Logo';
import { MaterialManagement } from './MaterialManagement';
import { ProductionPlanner } from './ProductionPlanner';
import { RecipeManagement } from './RecipeManagement';
import { RunningOrdersMatrix } from './RunningOrdersMatrix';
import { ScheduledOrders } from './ScheduledOrders';
import { UserManagement } from './UserManagement';
import { useScreenInit } from '../useScreenInit.js';
type OfficeSection = 'dashboard' | 'running-orders' | 'planned-orders' | 'planner' | 'recipes' | 'materials' | 'users' | 'settings';
type OfficeWorkspaceProps = {
  username: string;
  onLogout: () => void;
};
type NavigationItem = {
  id: OfficeSection;
  label: string;
  icon: typeof LayoutDashboardIcon;
};
type Kpi = {
  label: string;
  value: string;
  detail: string;
  tone: 'neutral' | 'success' | 'warning';
};
type OrderStatus = {
  id: string;
  product: string;
  progress: number;
  status: 'Aktiv' | 'Verzögert';
  detailLabel: string;
  detailValue: string;
};
const navigationItems: NavigationItem[] = [{
  id: 'running-orders',
  label: 'Aufträge',
  icon: ClipboardListIcon
}, {
  id: 'planner',
  label: 'Planer',
  icon: CalendarDaysIcon
}, {
  id: 'recipes',
  label: 'Produkte',
  icon: BookOpenIcon
}, {
  id: 'materials',
  label: 'Rohstoffe',
  icon: PackageIcon
}, {
  id: 'users',
  label: 'Benutzer',
  icon: UsersIcon
}, {
  id: 'settings',
  label: 'Einstellungen',
  icon: SettingsIcon
}];
const kpis: Kpi[] = [{
  label: 'Aufträge',
  value: '6',
  detail: '+2 heute',
  tone: 'success'
}, {
  label: 'Geplante Aufträge',
  value: '12',
  detail: 'diese Woche',
  tone: 'neutral'
}, {
  label: 'Auslastung',
  value: '78%',
  detail: 'im Zielbereich',
  tone: 'success'
}, {
  label: 'Verzögerungen',
  value: '1',
  detail: 'benötigt Aufmerksamkeit',
  tone: 'warning'
}];
const orderStatuses: OrderStatus[] = [{
  id: 'AUF-240718-033',
  product: 'Bioland Honige',
  progress: 72,
  status: 'Aktiv',
  detailLabel: 'Fällig',
  detailValue: 'heute · 18:00 Uhr'
}, {
  id: 'AUF-240718-035',
  product: 'Brotaufstriche',
  progress: 45,
  status: 'Verzögert',
  detailLabel: 'Verzögerung',
  detailValue: '2 Stunden'
}];
export function OfficeWorkspace({
  username,
  onLogout
}: OfficeWorkspaceProps) {
  const screenInit = useScreenInit();
  const initialSection: OfficeSection = screenInit.officeSection === 'planner' ? 'planner' : screenInit.officeSection === 'users' ? 'users' : screenInit.officeSection === 'materials' ? 'materials' : screenInit.officeSection === 'recipes' ? 'recipes' : 'running-orders';
  const [activeSection, setActiveSection] = useState<OfficeSection>(initialSection);
  function openSection(section: OfficeSection) {
    setActiveSection(section);
  }
  if (activeSection === 'planner') {
    return <OfficeShell activeSection={activeSection} username={username} onLogout={onLogout} onNavigate={openSection} data-id="element-137">
        <ProductionPlanner data-id="element-138" />
      </OfficeShell>;
  }
  if (activeSection === 'running-orders') {
    return <OfficeShell activeSection={activeSection} username={username} onLogout={onLogout} onNavigate={openSection} data-id="element-139">
        <RunningOrdersMatrix data-id="element-140" />
      </OfficeShell>;
  }
  if (activeSection === 'planned-orders') {
    return <OfficeShell activeSection={activeSection} username={username} onLogout={onLogout} onNavigate={openSection} data-id="element-141">
        <ScheduledOrders onBack={() => setActiveSection('running-orders')} data-id="element-142" />
      </OfficeShell>;
  }
  if (activeSection === 'materials') {
    return <OfficeShell activeSection={activeSection} username={username} onLogout={onLogout} onNavigate={openSection} data-id="element-143">
        <MaterialManagement data-id="element-144" />
      </OfficeShell>;
  }
  if (activeSection === 'recipes') {
    return <OfficeShell activeSection={activeSection} username={username} onLogout={onLogout} onNavigate={openSection} data-id="element-145">
        <RecipeManagement data-id="element-146" />
      </OfficeShell>;
  }
  if (activeSection === 'users') {
    return <OfficeShell activeSection={activeSection} username={username} onLogout={onLogout} onNavigate={openSection} data-id="element-147">
        <UserManagement data-id="element-148" />
      </OfficeShell>;
  }
  return <OfficeShell activeSection={activeSection} username={username} onLogout={onLogout} onNavigate={openSection} data-id="element-149">
      {activeSection === 'dashboard' ? <Dashboard username={username} onOpenRunningOrders={() => openSection('running-orders')} onOpenPlannedOrders={() => openSection('planned-orders')} data-id="element-150" /> : <ComingSoon section={activeSection} data-id="element-151" />}
    </OfficeShell>;
}
type OfficeShellProps = {
  activeSection: OfficeSection;
  children: React.ReactNode;
  username: string;
  onLogout: () => void;
  onNavigate: (section: OfficeSection) => void;
};
function OfficeShell({
  activeSection,
  children,
  username,
  onLogout,
  onNavigate
}: OfficeShellProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const displayName = username.trim() || 'Max';
  const sidebarLayout = isCollapsed ? 'w-[88px] px-3 py-5' : 'w-[88px] px-3 py-5 sm:w-[240px] sm:px-5 sm:py-6';
  return <main className="flex min-h-screen w-full bg-surface font-body text-text-primary" data-id="element-152">
      <aside className={`sticky top-0 flex h-screen shrink-0 flex-col border-r border-border bg-surface transition-[width,padding] duration-200 ease-out ${sidebarLayout}`} data-id="element-153">
        <div className={`flex min-h-[44px] items-center ${isCollapsed ? 'justify-center' : 'justify-center sm:justify-between'}`} data-id="element-154">
          <Logo size="lg" alt="Sonnentracht" className={`h-8 w-auto sm:h-10 ${isCollapsed ? 'hidden' : ''}`} data-id="element-155" />
          <button type="button" onClick={() => setIsCollapsed(collapsed => !collapsed)} aria-label={isCollapsed ? 'Navigation ausklappen' : 'Navigation einklappen'} aria-expanded={!isCollapsed} title={isCollapsed ? 'Navigation ausklappen' : 'Navigation einklappen'} className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-brand-yellow-light hover:text-text-primary focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green sm:flex" data-id="element-156">
            {isCollapsed ? <PanelLeftOpenIcon size={20} aria-hidden="true" data-id="element-157" /> : <PanelLeftCloseIcon size={20} aria-hidden="true" data-id="element-158" />}
          </button>
        </div>

        <nav className="mt-8 flex-1" aria-label="Office-Navigation" data-id="element-159">
          <ul className="space-y-1.5" data-id="element-160">
            {navigationItems.map(item => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            return <li key={item.id} data-id="element-161">
                  <button type="button" onClick={() => onNavigate(item.id)} aria-current={isActive ? 'page' : undefined} title={item.label} className={`flex min-h-[48px] w-full items-center gap-3 rounded-lg px-3 text-left text-sm transition-colors focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green ${isCollapsed ? 'justify-center' : 'justify-center sm:justify-start sm:px-4'} ${isActive ? 'bg-brand-green font-heading text-text-primary shadow-sm' : 'text-text-secondary hover:bg-brand-yellow-light hover:text-text-primary'}`} data-id="element-162">
                    <Icon size={20} strokeWidth={isActive ? 2.2 : 1.8} aria-hidden="true" className="shrink-0" data-id="element-163" />
                    <span className={isCollapsed ? 'hidden' : 'hidden sm:inline'} data-id="element-164">
                      {item.label}
                    </span>
                  </button>
                </li>;
          })}
          </ul>
        </nav>

        <div className="border-t border-border pt-4" data-id="element-165">
          <div className={`flex items-center gap-3 ${isCollapsed ? 'justify-center' : 'justify-center sm:justify-start'}`} data-id="element-166">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-yellow-light text-text-primary" aria-hidden="true" data-id="element-167">
              <UserRoundIcon size={19} strokeWidth={1.8} data-id="element-168" />
            </div>
            <div className={isCollapsed ? 'hidden' : 'hidden min-w-0 sm:block'} data-id="element-169">
              <p className="truncate font-heading text-sm text-text-primary" data-id="element-170">
                {displayName}
              </p>
              <p className="text-xs text-text-secondary" data-id="element-171">Angemeldet</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout} className={`mt-3 w-full ${isCollapsed ? 'justify-center' : 'justify-center sm:justify-start'}`} leadingIcon={<LogOutIcon size={16} aria-hidden="true" data-id="element-173" />} aria-label={`${displayName} abmelden`} title="Abmelden" data-id="element-172">
            <span className={isCollapsed ? 'hidden' : 'hidden sm:inline'} data-id="element-174">
              Abmelden
            </span>
            <span className={isCollapsed ? 'sr-only' : 'sr-only sm:hidden'} data-id="element-175">
              Abmelden
            </span>
          </Button>
        </div>
      </aside>

      <div className="min-w-0 flex-1" data-id="element-176">{children}</div>
    </main>;
}
type DashboardProps = {
  username: string;
  onOpenRunningOrders: () => void;
  onOpenPlannedOrders: () => void;
};
function Dashboard({
  username,
  onOpenRunningOrders,
  onOpenPlannedOrders
}: DashboardProps) {
  const displayName = username.trim() || 'Max';
  return <div className="min-h-screen bg-surface-subtle" data-id="element-177">
      <header className="border-b border-border bg-surface px-5 py-6 sm:px-8 lg:px-10" data-id="element-178">
        <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        ease: 'easeOut'
      }} data-id="element-179">
          <p className="text-sm text-text-secondary" data-id="element-180">Office · Übersicht</p>
          <h1 className="mt-1 font-heading text-3xl leading-tight sm:text-4xl" data-id="element-181">
            Guten Morgen, {displayName}
          </h1>
        </motion.div>
      </header>

      <section className="mx-auto w-full max-w-7xl px-5 py-7 sm:px-8 sm:py-9 lg:px-10" data-id="element-182">
        <motion.section aria-label="Produktionskennzahlen" className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" initial="hidden" animate="visible" variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.06
          }
        }
      }} data-id="element-183">
          {kpis.map(kpi => <motion.div key={kpi.label} variants={{
          hidden: {
            opacity: 0,
            y: 12
          },
          visible: {
            opacity: 1,
            y: 0
          }
        }} transition={{
          duration: 0.26,
          ease: 'easeOut'
        }} data-id="element-184">
              <Card padding="lg" elevation="none" className="h-full border border-border bg-surface-subtle" data-id="element-185">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-186">
                  {kpi.label}
                </p>
                <p className="mt-3 font-heading text-4xl leading-none" data-id="element-187">
                  {kpi.value}
                </p>
                <p className={`mt-4 text-sm ${kpi.tone === 'warning' ? 'text-warning' : kpi.tone === 'success' ? 'text-success' : 'text-text-secondary'}`} data-id="element-188">
                  {kpi.detail}
                </p>
              </Card>
            </motion.div>)}
        </motion.section>

        <section className="mt-10" aria-labelledby="action-overview-title" data-id="element-189">
          <div className="flex flex-col gap-3 border-b border-border pb-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-190">
            <div data-id="element-191">
              <p className="text-sm text-text-secondary" data-id="element-192">
                Produktion im Blick behalten
              </p>
              <h2 id="action-overview-title" className="mt-1 font-heading text-2xl sm:text-3xl" data-id="element-193">
                Handlungsorientierte Übersicht
              </h2>
            </div>
            <Button variant="ghost" size="sm" onClick={onOpenRunningOrders} trailingIcon={<ChevronRightIcon size={17} aria-hidden="true" data-id="element-195" />} data-id="element-194">
              Alle laufenden Aufträge
            </Button>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2" data-id="element-196">
            {orderStatuses.map((order, index) => <motion.article key={order.id} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: 0.16 + index * 0.08,
            ease: 'easeOut'
          }} data-id="element-197">
                <Card padding="lg" elevation="sm" className="h-full border border-border bg-surface" data-id="element-198">
                  <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-199">
                    <div data-id="element-200">
                      <p className="font-heading text-lg" data-id="element-201">{order.id}</p>
                      <p className="mt-1 text-sm text-text-secondary" data-id="element-202">
                        {order.product}
                      </p>
                    </div>
                    <span className={`rounded-md px-3 py-1 text-xs font-semibold uppercase tracking-wide ${order.status === 'Aktiv' ? 'bg-success/15 text-success' : 'bg-brand-yellow-light text-warning'}`} data-id="element-203">
                      {order.status}
                    </span>
                  </div>

                  <div className="mt-7" data-id="element-204">
                    <div className="flex items-center justify-between gap-4 text-sm" data-id="element-205">
                      <span className="text-text-secondary" data-id="element-206">Fortschritt</span>
                      <span className="font-heading" data-id="element-207">{order.progress}%</span>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-surface-subtle" aria-label={`${order.progress} Prozent abgeschlossen`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={order.progress} data-id="element-208">
                      <motion.div className={`h-full rounded-full ${order.status === 'Aktiv' ? 'bg-brand-green' : 'bg-warning'}`} initial={{
                    width: 0
                  }} animate={{
                    width: `${order.progress}%`
                  }} transition={{
                    duration: 0.7,
                    delay: 0.3 + index * 0.08,
                    ease: 'easeOut'
                  }} data-id="element-209" />
                    </div>
                  </div>

                  <dl className="mt-6 flex items-center justify-between gap-4 border-t border-border pt-4 text-sm" data-id="element-210">
                    <dt className="text-text-secondary" data-id="element-211">{order.detailLabel}</dt>
                    <dd className="font-heading text-right" data-id="element-212">
                      {order.detailValue}
                    </dd>
                  </dl>

                  <Button size="sm" className="mt-6" onClick={onOpenRunningOrders} data-id="element-213">
                    {order.status === 'Aktiv' ? 'Auftrag öffnen' : 'Auftrag anpassen'}
                  </Button>
                </Card>
              </motion.article>)}
          </div>
        </section>

        <section className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-[1.4fr_1fr]" data-id="element-214">
          <Card padding="lg" elevation="none" className="border border-border bg-surface" data-id="element-215">
            <div className="flex items-center justify-between gap-4" data-id="element-216">
              <div data-id="element-217">
                <h2 className="font-heading text-xl" data-id="element-218">Nächste Planung</h2>
                <p className="mt-1 text-sm text-text-secondary" data-id="element-219">
                  12 Aufträge sind für diese Woche freigegeben.
                </p>
              </div>
              <CalendarDaysIcon size={24} className="text-text-secondary" aria-hidden="true" data-id="element-220" />
            </div>
            <Button variant="secondary" size="sm" className="mt-6" onClick={onOpenPlannedOrders} data-id="element-221">
              Geplante Aufträge prüfen
            </Button>
          </Card>

          <Card padding="lg" elevation="none" className="border border-border bg-brand-yellow-light" data-id="element-222">
            <div className="flex items-start gap-3" data-id="element-223">
              <DatabaseIcon size={23} className="mt-0.5 shrink-0" aria-hidden="true" data-id="element-224" />
              <div data-id="element-225">
                <h2 className="font-heading text-xl" data-id="element-226">Rohstoffhinweis</h2>
                <p className="mt-1 text-sm leading-6 text-text-secondary" data-id="element-227">
                  Ein Auftrag benötigt vor der nächsten Schicht noch eine
                  Materialprüfung.
                </p>
              </div>
            </div>
          </Card>
        </section>
      </section>
    </div>;
}
type ComingSoonProps = {
  section: Exclude<OfficeSection, 'dashboard' | 'running-orders' | 'planned-orders'>;
};
function ComingSoon({
  section
}: ComingSoonProps) {
  const item = navigationItems.find(navigationItem => navigationItem.id === section);
  const Icon = item?.icon ?? LayoutDashboardIcon;
  return <div className="flex min-h-screen items-center justify-center bg-surface-subtle px-5 py-10 sm:px-8" data-id="element-228">
      <motion.section aria-labelledby="coming-soon-title" className="w-full max-w-xl rounded-xl border border-border bg-surface p-7 text-center shadow-sm sm:p-10" initial={{
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} data-id="element-229">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg bg-brand-yellow-light" data-id="element-230">
          <Icon size={27} strokeWidth={1.8} aria-hidden="true" data-id="element-231" />
        </div>
        <h1 id="coming-soon-title" className="mt-6 font-heading text-3xl" data-id="element-232">
          {item?.label}
        </h1>
        <p className="mx-auto mt-3 max-w-md text-base leading-6 text-text-secondary" data-id="element-233">
          Dieser Bereich wird für die Office-Anwendung vorbereitet. Die
          Auftragsübersichten stehen bereits über die Navigation bereit.
        </p>
      </motion.section>
    </div>;
}